import type { MatchDoc } from './match'

// Result recording — pure logic (spec/usersmatch/05). Any of the match's
// players records once the start time has passed; the ORGANIZER may correct
// a wrong result; nobody else can touch one that exists. Recording completes
// the match (terminal).
//
// Persistence shape: Firestore forbids nested arrays, so on-court teams are
// stored as a FLAT 4-seat array — seats[0..1] = pair A, seats[2..3] = pair B
// (caught live 2026-07-05; the [[a,b],[c,d]] shape cannot be written).

export const PAIRINGS = [
  [[0, 1], [2, 3]],
  [[0, 2], [1, 3]],
  [[0, 3], [1, 2]],
] as const

export type ResultBlock = 'not-player' | 'too-early' | 'already-recorded'

export function resultBlockReason(doc: MatchDoc, uid: string, now: Date): ResultBlock | null {
  if (!doc.players.includes(uid)) return 'not-player'
  if (doc.startDateTime.getTime() > now.getTime()) return 'too-early'
  if (doc.result != null && doc.organizer !== uid) return 'already-recorded'
  return null
}

export interface RecordResultInput {
  /** Which of the 3 possible 2v2 pairings was on court. */
  pairingIndex: 0 | 1 | 2
  winnerPair: 0 | 1
  recordedBy: string
  /** Uids flagged absent (spec/06). Sanitized by the builder: only seated
   *  players, never the recorder. */
  noShows?: string[]
  now: Date
}

/** The two on-court pairs, reconstructed from the flat seat array. */
export function resultPairs(seats: (string | null)[]): (string | null)[][] {
  return [seats.slice(0, 2), seats.slice(2, 4)]
}

/**
 * Seats derive from the roster's join order via the pairing index; a seat
 * held by an off-app companion (no uid at that index) records as null — a
 * result is valid even when not all four are app players.
 */
export function buildResultPatch(doc: MatchDoc, input: RecordResultInput): Record<string, unknown> {
  const pairing = PAIRINGS[input.pairingIndex]
  const seats = pairing.flatMap((pair) => pair.map((i) => doc.players[i] ?? null))
  // Sanitize flags: only seated APP players (guests have no account to
  // flag), and never the recorder (self-flagging opens a griefing path).
  const noShows = (input.noShows ?? []).filter(
    (uid) =>
      uid !== input.recordedBy &&
      seats.includes(uid) &&
      doc.playersInfo[uid]?.guest !== true,
  )
  return {
    result: {
      seats,
      pairingIndex: input.pairingIndex,
      winnerPair: input.winnerPair,
      recordedBy: input.recordedBy,
      recordedAt: input.now,
      ...(noShows.length > 0 ? { noShows } : {}),
    },
    status: 'completed',
  }
}

/** Winner names for display, resolved through playersInfo. */
export function resultWinnerNames(doc: MatchDoc): string[] {
  const r = doc.result
  if (!r) return []
  return (resultPairs(r.seats)[r.winnerPair] ?? [])
    .filter((uid): uid is string => uid != null)
    .map((uid) => doc.playersInfo[uid]?.name ?? 'Jugador')
}
